import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import {
  CapaErrorCode,
  CapaQuotationUserVerificationType,
  FetchMyQuotationUserQuery,
  useCanEditRefundAccountLazyQuery,
  useDeleteMyRefundAccountByQuotationUserMutation,
  useFetchMyQuotationUserQuery,
  useFetchMyRefundBankAccountsQuery,
  useUpdateIdentityVerificationMutation
} from '@generated/graphql';
import useIamport from '@hooks/useIamport';
import { Box, makeStyles, TypographyTypeMap } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { PreventEditDialog } from '../Dialog/RequestActionDialog';

const PhoneNumberItem = () => {
  const { VerificationButton } = useStyles();
  const { certificate } = useIamport();
  const { enqueueSnackbar } = useSnackbar();
  const [preventEditModalOpen, setPreventEditModalOpen] = useState(false);
  const { data: myQuotationUser } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });

  const {
    buttonLabel,
    label,
    value,
    valueColor,
    hasRequiredInfo
  } = getTextByVerificationType(myQuotationUser?.fetchMyQuotationUser.data);

  const [updateIdentityVerification] = useUpdateIdentityVerificationMutation({
    refetchQueries: ['fetchMyQuotationUser']
  });
  const [canEditRefundAccount] = useCanEditRefundAccountLazyQuery({
    fetchPolicy: 'network-only'
  });
  const { data: myRefundBankAccounts } = useFetchMyRefundBankAccountsQuery();
  const refundBankAccountIds = (
    myRefundBankAccounts?.fetchMyRefundBankAccounts.data ?? []
  ).map((bankAccountsInfo) => bankAccountsInfo.id);

  const [
    deleteMyRefundAccountByQuotationUser
  ] = useDeleteMyRefundAccountByQuotationUserMutation({
    refetchQueries: ['fetchMyRefundBankAccounts', 'fetchMyQuotationUser']
  });

  const handleClick = async () => {
    try {
      if (!hasRequiredInfo) {
        throw Error('필수정보 입력 후 본인 인증 해주세요.');
      }
      const { data: canEditRefundAccountData } = await canEditRefundAccount();
      const canEdit =
        canEditRefundAccountData?.canEditRefundAccount.data
          ?.canEditRefundAccount ?? false;

      if (!canEdit) {
        setPreventEditModalOpen(true);
        return;
      }

      const certificateResponse = await certificate();

      if (!certificateResponse.success || !certificateResponse.imp_uid) {
        throw Error(certificateResponse.error_msg);
      }

      const result = await updateIdentityVerification({
        variables: {
          input: {
            impUid: certificateResponse.imp_uid
          }
        }
      });

      if (
        result.data?.updateIdentityVerification.result !== CapaErrorCode.Success
      ) {
        throw Error(result.data?.updateIdentityVerification.result);
      }

      refundBankAccountIds.forEach((refundBankAccountId) =>
        deleteMyRefundAccountByQuotationUser({
          variables: { input: { refundBankAccountId } }
        })
      );

      enqueueSnackbar('휴대폰 번호가 변경되었습니다.');
    } catch (error) {
      const errorMessage =
        (error as { message?: string }).message ?? '본인 인증 실패입니다.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      <Box>
        <CustomizedTypography
          variant={'body1'}
          color={'textSecondary'}
          gutterBottoms={8}
        >
          {label}
        </CustomizedTypography>

        <CustomizedTypography
          variant={'body1'}
          fontWeight="500"
          color={valueColor}
        >
          {value}
        </CustomizedTypography>
      </Box>
      <Box>
        <CustomizedButton
          size="small"
          variant="outlined"
          color="dark"
          className={VerificationButton}
          onClick={handleClick}
        >
          {buttonLabel}
        </CustomizedButton>
      </Box>
      <PreventEditDialog
        open={preventEditModalOpen}
        onClose={() => setPreventEditModalOpen(false)}
        description={`휴대폰 번호 수정이 불가능합니다. \n 파트너와 거래를 완료하거나 거래취소를 진행해주세요.`}
      />
    </Box>
  );
};

export default PhoneNumberItem;

const useStyles = makeStyles({
  VerificationButton: {
    height: 40
  }
});

const getTextByVerificationType = (
  quotationUser?: FetchMyQuotationUserQuery['fetchMyQuotationUser']['data']
) => {
  const isVerified =
    quotationUser?.verificationType ===
    CapaQuotationUserVerificationType.Verified;
  const cellphoneNumber = quotationUser?.cellphoneNumber ?? '0';

  const label = isVerified ? '휴대폰 번호' : '휴대폰 본인 인증';
  const value = isVerified
    ? cellphoneNumber
    : '원활한 서비스 이용을 위하여 인증을 완료해 주세요.';
  const buttonLabel = isVerified ? '재인증' : '인증하기';
  const valueColor: TypographyTypeMap['props']['color'] = isVerified
    ? 'textPrimary'
    : 'error';
  const hasRequiredInfo =
    quotationUser?.cellphoneNumber && quotationUser?.managerName;

  return {
    label,
    value,
    buttonLabel,
    valueColor,
    hasRequiredInfo
  };
};
